
<template>
  <div
    v-if="isAccessChecked && isAdmin"
    class="ae-page"
    :class="{ 'ae-page--dark': isDark }"
  >
    <header class="ae-fixed-header">
    <DefaultHeader
      title="Редактирование вакансии"
      :back="true"
      subtitle="Описание вакансии"
      :offset="680"
    />
  </header>

  <main class="ae-main">
      <div class="ae-container">
        <header class="ae-hero">
          <div class="ae-hero__copy">
            <span class="ae-eyebrow">
              <span class="ae-eyebrow__dot"></span>
              ПАНЕЛЬ АДМИНИСТРАТОРА
            </span>

            <h1>Редактирование вакансии</h1>

            <p>
              Настройте описание, условия и оформление публикации.
            </p>
          </div>

          <div
            v-if="isReady"
            class="ae-hero__actions"
          >
            <span
              class="ae-state"
              :class="{ 'ae-state--changed': hasChanges }"
            >
              <span class="ae-state__dot"></span>
              {{
                isSaving
                  ? "Сохранение..."
                  : hasChanges
                    ? "Есть изменения"
                    : "Всё сохранено"
              }}
            </span>

            <button
              type="button"
              class="ae-button ae-button--primary"
              :disabled="!canSave"
              @click="saveVacancy"
            >
              <span
                v-if="isSaving"
                class="ae-spinner"
              ></span>

              {{ isSaving ? "Сохраняем" : "Сохранить" }}
            </button>
          </div>
        </header>

        <!-- ERROR -->

        <section
          v-if="loadError"
          class="ae-panel ae-error-state"
          role="alert"
        >
          <div class="ae-error-state__symbol">!</div>

          <h2>Не удалось открыть вакансию</h2>
          <p>{{ loadError }}</p>
        </section>

        <!-- EDITOR -->

        <div
          v-else-if="isReady"
          class="ae-layout"
        >
          <div class="ae-form-column">
            <!-- BASIC INFORMATION -->

            <section class="ae-panel">
              <div class="ae-panel__heading">
                <span class="ae-step">01</span>

                <div>
                  <span class="ae-kicker">О ВАКАНСИИ</span>

                  <h2>Основная информация</h2>

                  <p>
                    То, что кандидаты увидят в первую очередь.
                  </p>
                </div>
              </div>

              <div class="ae-field">
                <label class="ae-label">
                  Название вакансии
                  <span class="ae-required">*</span>
                </label>

                <TextField
                  v-model="vacancy.title"
                  class="ae-text-field"
                  placeholder="Например, Senior iOS Developer"
                />
              </div>

              <div class="ae-field">
                <div class="ae-field__heading">
                  <label
                    class="ae-label"
                    for="ae-description"
                  >
                    Описание вакансии
                  </label>

                  <span class="ae-counter">
                    {{ descriptionLength }} симв.
                  </span>
                </div>

                <textarea
                  id="ae-description"
                  v-model="vacancy.info"
                  class="ae-textarea"
                  rows="12"
                  placeholder="Расскажите о задачах, ожиданиях и преимуществах работы..."
                ></textarea>
              </div>
            </section>

            <!-- CONDITIONS -->

            <section class="ae-panel">
              <div class="ae-panel__heading">
                <span class="ae-step ae-step--violet">
                  02
                </span>

                <div>
                  <span class="ae-kicker">УСЛОВИЯ</span>

                  <h2>Зарплата и опыт</h2>

                  <p>
                    Условия, по которым кандидат оценит предложение.
                  </p>
                </div>
              </div>

              <div class="ae-experience">
                <div class="ae-experience__top">
                  <div>
                    <label
                      for="ae-experience"
                      class="ae-label"
                    >
                      Опыт работы
                    </label>

                    <p class="ae-help">
                      Минимальный необходимый опыт
                    </p>
                  </div>

                  <span class="ae-experience__value">
                    {{ experienceLabel }}
                  </span>
                </div>

                <input
                  id="ae-experience"
                  v-model.number="vacancy.experience"
                  class="ae-range"
                  type="range"
                  min="0"
                  max="10"
                  step="1"
                  :style="experienceTrackStyle"
                />

                <div class="ae-range__marks">
                  <span>Без опыта</span>
                  <span>5 лет</span>
                  <span>10+ лет</span>
                </div>
              </div>

              <div class="ae-pay-grid">
                <div class="ae-field">
                  <label class="ae-label">
                    Зарплата
                  </label>

                  <TextField
                    v-model="vacancy.salary"
                    class="ae-text-field"
                    placeholder="Например, 250 000"
                  />
                </div>

                <div class="ae-field">
                  <label class="ae-label">
                    Валюта
                  </label>

                  <div
                    class="ae-currencies"
                    role="group"
                    aria-label="Валюта зарплаты"
                  >
                    <button
                      v-for="item in currencies"
                      :key="item"
                      type="button"
                      :aria-pressed="vacancy.currency === item"
                      :class="[
                        'ae-currency',
                        {
                          'ae-currency--active':
                            vacancy.currency === item
                        }
                      ]"
                      @click="vacancy.currency = item"
                    >
                      {{ item }}
                    </button>
                  </div>
                </div>
              </div>

              <div class="ae-field">
                <label class="ae-label">
                  Специализация
                </label>

                <SpecificDropdown
                  v-model="vacancy.specific"
                />
              </div>
            </section>

            <!-- SEARCH & LOCATION -->

            <section class="ae-panel">
              <div class="ae-panel__heading">
                <span class="ae-step ae-step--amber">
                  03
                </span>

                <div>
                  <span class="ae-kicker">НАСТРОЙКИ</span>

                  <h2>Поиск и расположение</h2>

                  <p>
                    Помогите подходящим людям найти вашу вакансию.
                  </p>
                </div>
              </div>

              <!-- HASHTAGS -->

              <div class="ae-field">
                <label class="ae-label">
                  Хэштеги
                </label>

                <button
                  type="button"
                  class="ae-tags-button"
                  @click="isHashtagDrawerOpen = true"
                >
                  <span class="ae-tags-button__content">
                    <span
                      v-if="normalizedHashtags.length"
                      class="ae-tag-list"
                    >
                      <span
                        v-for="(tag, index) in normalizedHashtags.slice(0, 5)"
                        :key="`${tag}-${index}`"
                        class="ae-tag"
                      >
                        #{{ tag }}
                      </span>

                      <span
                        v-if="normalizedHashtags.length > 5"
                        class="ae-tag ae-tag--more"
                      >
                        +{{ normalizedHashtags.length - 5 }}
                      </span>
                    </span>

                    <span
                      v-else
                      class="ae-muted"
                    >
                      Выбрать хэштеги для поиска
                    </span>
                  </span>

                  <span class="ae-chevron">
                    ›
                  </span>
                </button>
              </div>

              <div class="ae-divider"></div>

              <!-- WORK FORMAT -->

              <div class="ae-toggle-list">
                <label class="ae-toggle-row">
                  <span class="ae-toggle-row__text">
                    <strong>Удалённая работа</strong>

                    <small>
                      Можно работать без посещения офиса
                    </small>
                  </span>

                  <input
                    v-model="vacancy.isRemote"
                    type="checkbox"
                  />

                  <span
                    class="ae-switch"
                    aria-hidden="true"
                  ></span>
                </label>

                <label
                  class="ae-toggle-row"
                  :class="{
                    'ae-toggle-row--disabled':
                      vacancy.isRemote
                  }"
                >
                  <span class="ae-toggle-row__text">
                    <strong>Адрес вручную</strong>

                    <small>
                      Указать адрес рабочего места
                    </small>
                  </span>

                  <input
                    v-model="isManual"
                    type="checkbox"
                    :disabled="vacancy.isRemote"
                  />

                  <span
                    class="ae-switch"
                    aria-hidden="true"
                  ></span>
                </label>
              </div>

              <!-- REMOTE -->

              <div
                v-if="vacancy.isRemote"
                class="ae-remote-note"
              >
                <span class="ae-remote-note__icon">
                  ⌁
                </span>

                <div>
                  <strong>Удалённый формат</strong>

                  <p>
                    Адрес офиса не нужен для этой вакансии.
                  </p>
                </div>
              </div>

              <!-- ADDRESS -->

              <div
                v-else
                class="ae-address"
              >
                <label
                  for="ae-address"
                  class="ae-label"
                >
                  Адрес рабочего места
                </label>

                <input
                  id="ae-address"
                  v-model="address"
                  class="ae-input"
                  type="text"
                  :disabled="!isManual"
                  placeholder="Город, улица, дом"
                />

                <div
                  class="ae-map"
                  :class="{
                    'ae-map--disabled': !isManual
                  }"
                >
                  <iframe
                    :src="mapUrl"
                    title="Карта адреса вакансии"
                    loading="lazy"
                    referrerpolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>
              </div>
            </section>
          </div>

          <!-- RIGHT SIDEBAR -->

          <aside class="ae-sidebar">
            <!-- COVER IMAGE -->

            <section class="ae-panel ae-media-panel">
              <div class="ae-sidebar-heading">
                <div>
                  <span class="ae-kicker">
                    ОФОРМЛЕНИЕ
                  </span>

                  <h2>Обложка вакансии</h2>
                </div>

                <span class="ae-sidebar-heading__symbol">
                  ▧
                </span>
              </div>

              <p class="ae-media-description">
                Изображение показывается в карточке вакансии.
              </p>

              <div
                class="ae-upload"
                @dragover.prevent
                @drop.prevent="handleDrop"
              >
                <input
                  ref="fileInput"
                  class="ae-file-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  @change="handleFileChange"
                />

                <!-- IMAGE PREVIEW -->

                <div
                  v-if="imageUrl"
                  class="ae-media-preview"
                >
                  <div
                    class="ae-media-preview__visual"
                    role="img"
                    aria-label="Обложка вакансии"
                    :style="imageBackgroundStyle"
                  >
                    <div
                      v-if="imageUploading"
                      class="ae-media-preview__loading"
                    >
                      <span class="ae-spinner"></span>
                      <span>Загрузка...</span>
                    </div>
                  </div>

                  <div class="ae-media-preview__buttons">
                    <button
                      class="ae-button ae-button--soft"
                      type="button"
                      :disabled="imageUploading || isSaving"
                      @click="triggerFileInput"
                    >
                      Заменить
                    </button>

                    <button
                      class="ae-button ae-button--danger"
                      type="button"
                      :disabled="imageUploading || isSaving"
                      @click="removeImage"
                    >
                      Удалить
                    </button>
                  </div>
                </div>

                <!-- EMPTY UPLOAD -->

                <button
                  v-else
                  type="button"
                  class="ae-upload-empty"
                  :disabled="imageUploading || isSaving"
                  @click="triggerFileInput"
                >
                  <span class="ae-upload-empty__icon">
                    <span
                      v-if="imageUploading"
                      class="ae-spinner"
                    ></span>

                    <span v-else>
                      ↑
                    </span>
                  </span>

                  <strong>
                    {{
                      imageUploading
                        ? "Загрузка..."
                        : "Добавить фото"
                    }}
                  </strong>

                  <small>
                    Нажмите или перетащите файл
                  </small>

                  <span class="ae-upload-empty__types">
                    JPG · PNG · WEBP
                  </span>
                </button>
              </div>

              <p
                v-if="imageError"
                class="ae-error-text"
                role="alert"
              >
                {{ imageError }}
              </p>

              <p class="ae-media-hint">
                Изображение не обрезается и целиком
                помещается в область предпросмотра.
              </p>
            </section>

            <!-- SUMMARY -->

            <section class="ae-panel ae-summary">
              <div class="ae-sidebar-heading">
                <div>
                  <span class="ae-kicker">
                    ПРЕДПРОСМОТР
                  </span>

                  <h2>Краткая сводка</h2>
                </div>
              </div>

              <h3 class="ae-summary__title">
                {{ vacancy.title || "Без названия" }}
              </h3>

              <dl class="ae-summary__list">
                <div>
                  <dt>Опыт</dt>
                  <dd>{{ experienceLabel }}</dd>
                </div>

                <div>
                  <dt>Оплата</dt>
                  <dd>
                    {{
                      vacancy.salary
                        ? `${vacancy.salary} ${vacancy.currency}`
                        : "Не указана"
                    }}
                  </dd>
                </div>

                <div>
                  <dt>Формат</dt>
                  <dd>
                    {{
                      vacancy.isRemote
                        ? "Удалённо"
                        : "На месте"
                    }}
                  </dd>
                </div>

                <div>
                  <dt>Хэштеги</dt>
                  <dd>{{ normalizedHashtags.length }}</dd>
                </div>

                <div>
                  <dt>ID вакансии</dt>

                  <dd
                    class="ae-summary__id"
                    :title="String(vacancy.id)"
                  >
                    {{ vacancy.id || "—" }}
                  </dd>
                </div>
              </dl>
            </section>

            <!-- SAVE -->

            <div class="ae-sidebar-save">
              <button
                class="ae-button ae-button--primary ae-sidebar-save__button"
                type="button"
                :disabled="!canSave"
                @click="saveVacancy"
              >
                <span
                  v-if="isSaving"
                  class="ae-spinner"
                ></span>

                {{
                  isSaving
                    ? "Сохраняем..."
                    : "Сохранить изменения"
                }}
              </button>

              <p>
                Сохраните изменения, чтобы обновить вакансию.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </main>

    <div class="ae-fixed-footer">
      <Footer />
    </div>

    <!-- HASHTAG DRAWER -->

    <HashtagDrawer
      v-if="isHashtagDrawerOpen"
      :model-value="vacancy.hashtags"
      @update:modelValue="updateHashtags"
      @close="isHashtagDrawerOpen = false"
    />

    <!-- SUCCESS TOAST -->

    <Transition name="ae-toast">
      <div
        v-if="showSnackbar"
        class="ae-toast"
        role="status"
      >
        <span class="ae-toast__check">
          ✓
        </span>

        <span>
          <strong>Вакансия сохранена</strong>
          <small>Все изменения применены</small>
        </span>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref
} from "vue";

import { waitForAuthReady } from "../utils/auth.js";

import DefaultHeader from "../components/AdminEditVacancyHeader.vue";
import TextField from "../components/UniversalTextField.vue";
import SpecificDropdown from "../components/SpecificDropdown.vue";
import Footer from "../components/MiniFooter.vue";
import MyVacanciesService from "../api/MyVacanciesService";
import HashtagDrawer from "../views/HashtagDrawer.vue";

const isAccessChecked = ref(false);
const isAdmin = ref(false);

// MARK: - Access

const isReady = ref(false);
const loadError = ref("");
const isSaving = ref(false);
const isDark = ref(false);

const storageKey = ref("admin_vacancy_to_edit");
const originalState = ref(null);

const currencies = ["₽", "$", "€"];

// MARK: - Vacancy

const vacancy = reactive({
  id: "",
  title: "",
  info: "",
  salary: "",
  currency: "₽",
  experience: 0,
  hashtags: [],
  specific: {
    id: "",
    name: "",
    eng_name: ""
  },
  isRemote: false,
  street: ""
});

const address = ref("");
const isManual = ref(false);

// MARK: - Hashtags

const isHashtagDrawerOpen = ref(false);

// MARK: - Image

const fileInput = ref(null);
const imageUrl = ref("");
const imageUploading = ref(false);
const imageError = ref("");

// MARK: - Snackbar

const showSnackbar = ref(false);

let previewObjectUrl = null;
let snackbarTimer = null;
let themeObserver = null;
let unmounted = false;

// MARK: - Computed

const descriptionLength = computed(() => {
  return String(vacancy.info || "").length;
});

const normalizedHashtags = computed(() => {
  const hashtags = Array.isArray(vacancy.hashtags)
    ? vacancy.hashtags
    : [];

  return hashtags
    .map(tag => {
      if (typeof tag === "string") {
        return tag.replace(/^#/, "").trim();
      }

      return String(
        tag?.name ||
        tag?.title ||
        ""
      )
        .replace(/^#/, "")
        .trim();
    })
    .filter(Boolean);
});

const experienceLabel = computed(() => {
  const n = Number(vacancy.experience) || 0;

  if (n === 0) {
    return "Без опыта";
  }

  if (n % 10 === 1 && n % 100 !== 11) {
    return `${n} год`;
  }

  if (
    [2, 3, 4].includes(n % 10) &&
    ![12, 13, 14].includes(n % 100)
  ) {
    return `${n} года`;
  }

  return `${n} лет`;
});

const experienceTrackStyle = computed(() => ({
  "--ae-range-progress": `${
    Math.max(
      0,
      Math.min(
        100,
        Number(vacancy.experience) * 10
      )
    )
  }%`
}));

const mapUrl = computed(() => {
  const location =
    address.value.trim() ||
    String(vacancy.street || "").trim() ||
    "Москва";

  return (
    "https://www.google.com/maps?q=" +
    encodeURIComponent(location) +
    "&output=embed"
  );
});

// Image is painted as CSS background.
// Its intrinsic dimensions cannot stretch the sidebar.

const imageBackgroundStyle = computed(() => ({
  backgroundImage: imageUrl.value
    ? `url(${JSON.stringify(imageUrl.value)})`
    : "none"
}));

// MARK: - State

const currentState = computed(() => ({
  title: vacancy.title,
  info: vacancy.info,
  salary: vacancy.salary,
  currency: vacancy.currency,
  experience: Number(vacancy.experience),
  hashtags: normalizedHashtags.value,

  specific: {
    id: vacancy.specific?.id || "",
    name: vacancy.specific?.name || "",
    eng_name: vacancy.specific?.eng_name || ""
  },

  isRemote: Boolean(vacancy.isRemote),
  address: address.value,
  isManual: isManual.value,
  imageUrl: imageUrl.value
}));

function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

const hasChanges = computed(() => {
  if (
    !isReady.value ||
    originalState.value === null
  ) {
    return false;
  }

  return (
    JSON.stringify(currentState.value) !==
    JSON.stringify(originalState.value)
  );
});

const canSave = computed(() => {
  return (
    hasChanges.value &&
    !isSaving.value &&
    !imageUploading.value &&
    Boolean(String(vacancy.title || "").trim())
  );
});

// MARK: - Hashtags

function updateHashtags(value) {
  vacancy.hashtags = Array.isArray(value)
    ? value
    : [];
}

// MARK: - Theme

function syncTheme() {
  isDark.value =
    document.documentElement.getAttribute("data-theme") === "dark";
}

// MARK: - Image Upload

function triggerFileInput() {
  if (
    !imageUploading.value &&
    !isSaving.value
  ) {
    fileInput.value?.click();
  }
}

function revokePreview() {
  if (previewObjectUrl) {
    URL.revokeObjectURL(previewObjectUrl);
  }

  previewObjectUrl = null;
}

function uploadedImageUrl(result) {
  if (typeof result === "string") {
    return result;
  }

  return (
    result?.url ||
    result?.image_url ||
    result?.imageUrl ||
    result?.data?.url ||
    result?.data?.image_url ||
    result?.data?.imageUrl ||
    ""
  );
}

async function uploadImage(selectedFile) {
  if (
    !selectedFile ||
    imageUploading.value ||
    isSaving.value
  ) {
    return;
  }

  const allowedTypes = [
    "image/jpeg",
    "image/png",
    "image/webp"
  ];

  if (!allowedTypes.includes(selectedFile.type)) {
    imageError.value =
      "Поддерживаются только JPG, PNG и WEBP.";
    return;
  }

  if (!vacancy.id) {
    imageError.value =
      "Не найден идентификатор вакансии.";
    return;
  }

  const previousUrl = imageUrl.value;

  imageError.value = "";
  imageUploading.value = true;

  revokePreview();

  previewObjectUrl = URL.createObjectURL(selectedFile);
  imageUrl.value = previewObjectUrl;

  try {
    const result =
      await MyVacanciesService.uploadVacancyImage(
        vacancy.id,
        selectedFile
      );

    const url = uploadedImageUrl(result);

    if (
      typeof url !== "string" ||
      !url ||
      url.startsWith("blob:")
    ) {
      throw new Error(
        "Сервер не вернул адрес загруженного изображения."
      );
    }

    if (unmounted) {
      return;
    }

    imageUrl.value = url;

    localStorage.setItem(
      "vacancy_image_url",
      url
    );
  } catch (error) {
    console.error(
      "Ошибка загрузки изображения:",
      error
    );

    if (!unmounted) {
      imageUrl.value = previousUrl;

      imageError.value =
        error?.response?.data?.error ||
        error?.message ||
        "Не удалось загрузить изображение.";
    }
  } finally {
    revokePreview();

    if (!unmounted) {
      imageUploading.value = false;
    }

    if (fileInput.value) {
      fileInput.value.value = "";
    }
  }
}

function handleFileChange(event) {
  const selectedFile = event.target.files?.[0];

  if (selectedFile) {
    void uploadImage(selectedFile);
  }
}

function handleDrop(event) {
  const selectedFile =
    event.dataTransfer?.files?.[0];

  if (selectedFile) {
    void uploadImage(selectedFile);
  }
}

function removeImage() {
  if (
    imageUploading.value ||
    isSaving.value
  ) {
    return;
  }

  revokePreview();

  imageUrl.value = "";
  imageError.value = "";

  if (fileInput.value) {
    fileInput.value.value = "";
  }

  localStorage.removeItem("vacancy_image_url");
}

// MARK: - Load Vacancy

function loadVacancy() {
  let raw = localStorage.getItem(
    "admin_vacancy_to_edit"
  );

  if (!raw) {
    raw = localStorage.getItem("vacancy_to_edit");

    storageKey.value = "vacancy_to_edit";
  }

  if (!raw) {
    loadError.value =
      "Данные вакансии не найдены. Вернитесь в список и откройте её заново.";
    return;
  }

  try {
    const data = JSON.parse(raw);

    if (
      !data ||
      typeof data !== "object" ||
      Array.isArray(data)
    ) {
      throw new Error(
        "Некорректные данные вакансии."
      );
    }

    const parsed = {
      ...data,

      hashtags: Array.isArray(data.hashtags)
        ? data.hashtags
        : [],

      specific:
        data.specific &&
        typeof data.specific === "object"
          ? data.specific
          : {
              id: "",
              name: "",
              eng_name: ""
            },

      isRemote: Boolean(
        data.isRemote ?? data.is_remote
      )
    };

    Object.assign(vacancy, parsed);

    imageUrl.value =
      parsed.image_url ||
      parsed.imageUrl ||
      "";

    const savedManual = localStorage.getItem(
      "vacancy_is_manual"
    );

    isManual.value =
      savedManual === null
        ? Boolean(parsed.street)
        : savedManual === "true";

    address.value =
      parsed.street ||
      localStorage.getItem("vacancy_address") ||
      "";

    originalState.value = clone(currentState.value);

    isReady.value = true;
    loadError.value = "";
  } catch (error) {
    console.error(
      "Ошибка чтения вакансии:",
      error
    );

    loadError.value =
      "Не удалось прочитать данные вакансии.";
  }
}

// MARK: - Save Vacancy

async function saveVacancy() {
  if (!canSave.value) {
    return;
  }

  const snapshot = clone(currentState.value);

  const payload = {
    vacancy_id: vacancy.id,

    title: String(vacancy.title || "").trim(),

    info: String(vacancy.info || "").trim(),

    salary: vacancy.salary ?? "",

    currency: vacancy.currency,

    image_url: imageUrl.value || "",

    specific: {
      id: vacancy.specific?.id || "",
      name: vacancy.specific?.name || "",
      eng_name: vacancy.specific?.eng_name || ""
    },

    hashtags: [...normalizedHashtags.value],

    is_remote: Boolean(vacancy.isRemote),

    street: vacancy.isRemote
      ? ""
      : isManual.value
        ? address.value.trim()
        : vacancy.street || "",

    experience: Number(vacancy.experience)
  };

  isSaving.value = true;

  try {
    await MyVacanciesService.updateVacancy(payload);

    originalState.value = snapshot;

    vacancy.street = payload.street;

    localStorage.setItem(
      storageKey.value,
      JSON.stringify({
        ...clone(vacancy),
        ...payload,
        isRemote: payload.is_remote
      })
    );

    localStorage.setItem(
      "vacancy_is_manual",
      String(isManual.value)
    );

    localStorage.setItem(
      "vacancy_address",
      address.value
    );

    showSnackbar.value = true;

    if (snackbarTimer) {
      clearTimeout(snackbarTimer);
    }

    snackbarTimer = window.setTimeout(() => {
      showSnackbar.value = false;
      snackbarTimer = null;
    }, 2800);
  } catch (error) {
    console.error(
      "Ошибка сохранения вакансии:",
      error
    );

    window.alert(
      error?.response?.data?.error ||
      error?.message ||
      "Не удалось сохранить вакансию."
    );
  } finally {
    isSaving.value = false;
  }
}

// MARK: - Lifecycle

onMounted(async () => {
  syncTheme();

  themeObserver = new MutationObserver(syncTheme);

  themeObserver.observe(
    document.documentElement,
    {
      attributes: true,
      attributeFilter: ["data-theme"]
    }
  );

  try {
    const user = await waitForAuthReady();

    if (
      !user ||
      localStorage.getItem("is_admin") !== "true"
    ) {
      window.location.replace(
        "https://hr3.space/"
      );

      return;
    }

    isAdmin.value = true;

    loadVacancy();
  } catch (error) {
    console.error(
      "Ошибка проверки доступа:",
      error
    );

    window.location.replace(
      "https://hr3.space/"
    );
  } finally {
    isAccessChecked.value = true;
  }
});

onBeforeUnmount(() => {
  unmounted = true;

  revokePreview();

  themeObserver?.disconnect();

  if (snackbarTimer) {
    clearTimeout(snackbarTimer);
  }
});
</script>

<style scoped>
/* ==========================================================
   COMPONENT THEME
   All styles are scoped to this component.
   ========================================================== */

.ae-page {
  --ae-bg: #f5f7fb;
  --ae-surface: #ffffff;
  --ae-text: #252d40;
  --ae-muted: #8c96a9;
  --ae-subtext: #657188;
  --ae-line: #e9edf4;
  --ae-input: #f8faff;
  --ae-blue: #496bd7;
  --ae-blue-soft: #edf1ff;
  --ae-shadow: 0 8px 38px rgba(40, 57, 95, 0.04);

  width: 100%;
  height: 100dvh;
  min-height: 0;

  display: flex;
  flex-direction: column;

  overflow: hidden;

  background: var(--ae-bg);
  color: var(--ae-text);
  font-family: "Eastman-Regular", sans-serif;
}

/* HEADER */

.ae-fixed-header {
  position: relative;
  z-index: 20;

  flex: 0 0 auto;
  width: 100%;
  min-height: 0;

  background: var(--ae-surface);
}

/* MAIN SCROLL AREA */

.ae-main {
  position: relative;

  flex: 1 1 auto;

  min-width: 0;
  min-height: 0;

  overflow-x: hidden;
  overflow-y: auto;

  overscroll-behavior-y: contain;
  -webkit-overflow-scrolling: touch;

  padding: 36px 24px 80px;
}

/* FOOTER */

.ae-fixed-footer {
  position: relative;
  z-index: 20;

  flex: 0 0 auto;
  width: 100%;
  min-height: 0;

  background: var(--ae-surface);
}

.ae-page--dark {
  --ae-bg: #171b23;
  --ae-surface: #222833;
  --ae-text: #eef1f8;
  --ae-muted: #9aa6b9;
  --ae-subtext: #bcc6d7;
  --ae-line: #363e4d;
  --ae-input: #272e3a;
  --ae-blue: #91a9ff;
  --ae-blue-soft: #303e5b;
  --ae-shadow: 0 8px 38px rgba(0, 0, 0, 0.10);
}

.ae-page,
.ae-page *,
.ae-page *::before,
.ae-page *::after {
  box-sizing: border-box;
}

/* ==========================================================
   PAGE
   ========================================================== */

.ae-main {
  flex: 1;
  min-width: 0;
  padding: 36px 24px 80px;
}

.ae-container {
  width: 100%;
  max-width: 1160px;
  min-width: 0;
  margin: 0 auto;
}

/* ==========================================================
   HERO
   ========================================================== */

.ae-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  padding: 8px 2px 30px;
}

.ae-hero__copy {
  min-width: 0;
}

.ae-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  color: var(--ae-blue);
  font-family: "Eastman-Medium", sans-serif;
  font-size: 10px;
  letter-spacing: 0.1em;
}

.ae-eyebrow__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--ae-blue);
  box-shadow: 0 0 0 4px var(--ae-blue-soft);
}

.ae-hero h1 {
  margin: 12px 0 6px;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 28px;
  font-weight: normal;
  line-height: 1.25;
  letter-spacing: -0.5px;
}

.ae-hero p {
  margin: 0;
  color: var(--ae-muted);
  font-size: 12px;
  line-height: 1.6;
}

.ae-hero__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.ae-state {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--ae-subtext);
  font-size: 11px;
  white-space: nowrap;
}

.ae-state__dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #40b78b;
}

.ae-state--changed .ae-state__dot {
  background: #f0b260;
}

/* ==========================================================
   BUTTONS
   ========================================================== */

.ae-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 43px;
  padding: 11px 18px;
  border: 1px solid transparent;
  border-radius: 12px;
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  font-weight: normal;
  line-height: 1.2;
  cursor: pointer;
  transition:
    transform 0.18s,
    opacity 0.18s,
    border-color 0.18s;
}

.ae-button:hover:not(:disabled) {
  transform: translateY(-1px);
}

.ae-button:disabled {
  opacity: 0.48;
  cursor: not-allowed;
}

.ae-button--primary {
  background: #4c6fda;
  color: #ffffff;
  box-shadow: 0 6px 14px rgba(57, 93, 192, 0.14);
}

.ae-button--soft {
  background: var(--ae-blue-soft);
  color: var(--ae-blue);
}

.ae-button--danger {
  background: #fff1f2;
  color: #c44c63;
}

/* ==========================================================
   MAIN GRID
   ========================================================== */

.ae-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 330px;
  gap: 22px;
  align-items: start;
  min-width: 0;
}

.ae-form-column,
.ae-sidebar {
  display: flex;
  flex-direction: column;
  gap: 19px;
  min-width: 0;
}

.ae-sidebar {
  position: sticky;
  top: 20px;
}

/* ==========================================================
   PANELS
   ========================================================== */

.ae-panel {
  width: 100%;
  min-width: 0;
  padding: 26px;
  border: 1px solid var(--ae-line);
  border-radius: 20px;
  background: var(--ae-surface);
  box-shadow: var(--ae-shadow);
}

.ae-panel__heading {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 26px;
}

.ae-step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 45px;
  width: 45px;
  height: 45px;
  border-radius: 14px;
  background: #edf3ff;
  color: #5276cf;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 14px;
  font-weight: normal;
}

.ae-step--violet {
  color: #8d70c9;
  background: #f1edff;
}

.ae-step--amber {
  color: #c28a43;
  background: #fff4e5;
}

.ae-kicker {
  display: block;
  margin-bottom: 5px;
  color: var(--ae-muted);
  font-family: "Eastman-Medium", sans-serif;
  font-size: 9px;
  font-weight: normal;
  letter-spacing: 0.11em;
}

.ae-panel__heading h2,
.ae-sidebar-heading h2 {
  margin: 0;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 16px;
  font-weight: normal;
  line-height: 1.35;
}

.ae-panel__heading p {
  margin: 5px 0 0;
  color: var(--ae-muted);
  font-size: 11px;
  line-height: 1.5;
}

/* ==========================================================
   INPUT FIELDS
   ========================================================== */

.ae-field {
  margin-top: 22px;
  min-width: 0;
}

.ae-panel__heading + .ae-field {
  margin-top: 0;
}

.ae-label {
  display: block;
  margin-bottom: 9px;
  color: var(--ae-subtext);
  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
  font-weight: normal;
}

.ae-required {
  color: #dc6271;
}

.ae-text-field {
  display: block;
  width: 100%;
  min-width: 0;
}

.ae-field__heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ae-counter {
  color: var(--ae-muted);
  font-size: 10px;
}

.ae-textarea,
.ae-input {
  display: block;
  width: 100%;
  min-width: 0;
  padding: 15px;
  border: 1px solid var(--ae-line);
  border-radius: 12px;
  outline: none;
  background: var(--ae-input);
  color: var(--ae-text);
  font-family: "Eastman-Regular", sans-serif;
  font-size: 12px;
  font-weight: normal;
  line-height: 1.7;
  transition: border-color 0.2s;
}

.ae-textarea {
  min-height: 260px;
  resize: vertical;
}

.ae-textarea:focus,
.ae-input:focus {
  border-color: #7899ec;
}

.ae-textarea::placeholder,
.ae-input::placeholder {
  color: var(--ae-muted);
}

.ae-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ==========================================================
   EXPERIENCE
   ========================================================== */

.ae-experience {
  margin-bottom: 30px;
}

.ae-experience__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  margin-bottom: 19px;
}

.ae-help {
  margin: -3px 0 0;
  color: var(--ae-muted);
  font-size: 10px;
}

.ae-experience__value {
  flex-shrink: 0;
  padding: 9px 12px;
  border-radius: 10px;
  background: var(--ae-blue-soft);
  color: var(--ae-blue);
  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
  font-weight: normal;
}

.ae-range {
  display: block;
  width: 100%;
  height: 5px;
  margin: 0;
  appearance: none;
  outline: none;
  border-radius: 9px;
  cursor: pointer;
  background: linear-gradient(
    90deg,
    #547be0 0%,
    #547be0 var(--ae-range-progress),
    var(--ae-line) var(--ae-range-progress),
    var(--ae-line) 100%
  );
}

.ae-range::-webkit-slider-thumb {
  width: 19px;
  height: 19px;
  appearance: none;
  border: 4px solid #547be0;
  border-radius: 50%;
  background: var(--ae-surface);
  box-shadow: 0 2px 8px rgba(84, 123, 224, 0.2);
}

.ae-range::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border: 4px solid #547be0;
  border-radius: 50%;
  background: var(--ae-surface);
}

.ae-range__marks {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  margin-top: 12px;
  color: var(--ae-muted);
  font-size: 10px;
}

/* ==========================================================
   SALARY
   ========================================================== */

.ae-pay-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 15px;
  align-items: end;
}

.ae-pay-grid .ae-field {
  margin-top: 0;
}

.ae-currencies {
  display: flex;
  gap: 3px;
  height: 44px;
  padding: 4px;
  border: 1px solid var(--ae-line);
  border-radius: 12px;
  background: var(--ae-input);
}

.ae-currency {
  min-width: 36px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--ae-muted);
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  font-weight: normal;
  cursor: pointer;
}

.ae-currency--active {
  background: var(--ae-surface);
  color: var(--ae-blue);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

/* ==========================================================
   HASHTAGS
   ========================================================== */

.ae-tags-button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  min-height: 58px;
  padding: 12px 13px;
  border: 1px solid var(--ae-line);
  border-radius: 12px;
  background: var(--ae-input);
  color: var(--ae-text);
  text-align: left;
  cursor: pointer;
}

.ae-tags-button__content {
  flex: 1;
  min-width: 0;
}

.ae-tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ae-tag {
  padding: 6px 8px;
  border-radius: 7px;
  background: var(--ae-blue-soft);
  color: var(--ae-blue);
  font-size: 10px;
}

.ae-tag--more {
  opacity: 0.8;
}

.ae-muted {
  color: var(--ae-muted);
  font-size: 11px;
}

.ae-chevron {
  flex-shrink: 0;
  color: var(--ae-muted);
  font-size: 25px;
  line-height: 1;
}

.ae-divider {
  height: 1px;
  margin: 28px 0 5px;
  background: var(--ae-line);
}

/* ==========================================================
   TOGGLES
   ========================================================== */

.ae-toggle-row {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 0;
  cursor: pointer;
}

.ae-toggle-row + .ae-toggle-row {
  border-top: 1px solid var(--ae-line);
}

.ae-toggle-row--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ae-toggle-row__text {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.ae-toggle-row__text strong {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  font-weight: normal;
}

.ae-toggle-row__text small {
  color: var(--ae-muted);
  font-size: 10px;
  line-height: 1.5;
}

.ae-toggle-row input {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
}

.ae-switch {
  position: relative;
  flex: 0 0 41px;
  height: 24px;
  border-radius: 18px;
  background: #cbd2df;
  transition: background 0.2s;
}

.ae-switch::after {
  content: "";
  position: absolute;
  left: 3px;
  top: 3px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.09);
  transition: transform 0.2s;
}

.ae-toggle-row input:checked + .ae-switch {
  background: #547be0;
}

.ae-toggle-row input:checked + .ae-switch::after {
  transform: translateX(17px);
}

.ae-toggle-row input:focus-visible + .ae-switch {
  outline: 3px solid #8faff7;
  outline-offset: 3px;
}

/* ==========================================================
   ADDRESS
   ========================================================== */

.ae-address {
  margin-top: 16px;
}

.ae-map {
  margin-top: 13px;
  overflow: hidden;
  border: 1px solid var(--ae-line);
  border-radius: 12px;
}

.ae-map--disabled {
  opacity: 0.45;
  pointer-events: none;
}

.ae-map iframe {
  display: block;
  width: 100%;
  height: 215px;
  border: 0;
}

.ae-remote-note {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
  padding: 16px;
  border-radius: 12px;
  background: var(--ae-blue-soft);
}

.ae-remote-note__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 35px;
  height: 35px;
  border-radius: 10px;
  background: var(--ae-surface);
  color: var(--ae-blue);
  font-size: 22px;
}

.ae-remote-note strong {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
  font-weight: normal;
}

.ae-remote-note p {
  margin: 4px 0 0;
  color: var(--ae-subtext);
  font-size: 10px;
}

/* ==========================================================
   SIDEBAR
   ========================================================== */

.ae-sidebar-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.ae-sidebar-heading__symbol {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 37px;
  height: 37px;
  border-radius: 10px;
  background: var(--ae-blue-soft);
  color: var(--ae-blue);
  font-size: 21px;
}

/* ==========================================================
   COVER IMAGE
   ========================================================== */

.ae-media-panel {
  overflow: hidden;
}

.ae-media-description {
  margin: 11px 0 20px;
  color: var(--ae-muted);
  font-size: 11px;
  line-height: 1.6;
}

.ae-upload,
.ae-media-preview {
  display: block;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.ae-file-input {
  display: none;
}

/*
 * Изображение — фон элемента.
 * Оно не участвует в вычислении ширины или высоты.
 */

.ae-media-preview__visual {
  position: relative;
  display: block;
  width: 100%;
  max-width: 100%;
  aspect-ratio: 4 / 3;
  overflow: hidden;
  border: 1px solid var(--ae-line);
  border-radius: 13px;
  background-color: var(--ae-input);
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
}

.ae-media-preview__loading {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: rgba(24, 34, 55, 0.72);
  color: #ffffff;
  font-size: 11px;
}

.ae-media-preview__buttons {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 9px;
  margin-top: 11px;
}

.ae-media-preview__buttons .ae-button {
  min-width: 0;
  padding: 10px 8px;
}

.ae-upload-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  min-height: 222px;
  padding: 22px 14px;
  border: 1px dashed #9eb3e3;
  border-radius: 13px;
  background: var(--ae-input);
  color: var(--ae-text);
  text-align: center;
  cursor: pointer;
}

.ae-upload-empty__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-bottom: 3px;
  border-radius: 14px;
  background: var(--ae-blue-soft);
  color: var(--ae-blue);
  font-size: 27px;
}

.ae-upload-empty strong {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 12px;
  font-weight: normal;
}

.ae-upload-empty small {
  color: var(--ae-muted);
  font-size: 10px;
}

.ae-upload-empty__types {
  margin-top: 5px;
  color: var(--ae-blue);
  font-size: 9px;
  letter-spacing: 0.05em;
}

.ae-upload-empty:disabled {
  opacity: 0.6;
  cursor: wait;
}

.ae-media-hint {
  margin: 15px 0 0;
  color: var(--ae-muted);
  font-size: 10px;
  line-height: 1.6;
}

.ae-error-text {
  margin: 12px 0 0;
  color: #cf5365;
  font-size: 11px;
  line-height: 1.5;
}

/* ==========================================================
   SUMMARY
   ========================================================== */

.ae-summary__title {
  margin: 21px 0 14px;
  overflow-wrap: anywhere;
  font-family: "Eastman-Bold", sans-serif;
  font-size: 15px;
  font-weight: normal;
  line-height: 1.5;
}

.ae-summary__list {
  margin: 0;
  padding: 0;
  border-top: 1px solid var(--ae-line);
}

.ae-summary__list > div {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  padding: 13px 0;
  border-bottom: 1px solid var(--ae-line);
  font-size: 11px;
}

.ae-summary__list dt {
  flex-shrink: 0;
  color: var(--ae-muted);
}

.ae-summary__list dd {
  min-width: 0;
  max-width: 62%;
  margin: 0;
  overflow: hidden;
  color: var(--ae-text);
  font-family: "Eastman-Medium", sans-serif;
  text-align: right;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ae-sidebar-save__button {
  width: 100%;
}

.ae-sidebar-save p {
  margin: 11px 0 0;
  color: var(--ae-muted);
  font-size: 10px;
  text-align: center;
  line-height: 1.5;
}

/* ==========================================================
   ERROR STATE
   ========================================================== */

.ae-error-state {
  max-width: 540px;
  margin: 65px auto;
  text-align: center;
}

.ae-error-state__symbol {
  display: grid;
  place-items: center;
  width: 48px;
  height: 48px;
  margin: 0 auto 14px;
  border-radius: 14px;
  background: #ffedf0;
  color: #cd5060;
  font-size: 25px;
}

.ae-error-state h2 {
  font-family: "Eastman-Bold", sans-serif;
  font-size: 16px;
  font-weight: normal;
}

.ae-error-state p {
  color: var(--ae-muted);
  font-size: 12px;
}

/* ==========================================================
   LOADING
   ========================================================== */

.ae-spinner {
  display: inline-block;
  width: 17px;
  height: 17px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: ae-spin 0.7s linear infinite;
}

@keyframes ae-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ==========================================================
   TOAST
   ========================================================== */

.ae-toast {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 260px;
  padding: 14px 17px;
  border-radius: 13px;
  background: #262f41;
  color: #ffffff;
  box-shadow: 0 15px 40px rgba(0, 0, 0, 0.15);
}

.ae-toast__check {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  background: #39b67d;
}

.ae-toast strong,
.ae-toast small {
  display: block;
}

.ae-toast strong {
  font-family: "Eastman-Medium", sans-serif;
  font-size: 11px;
  font-weight: normal;
}

.ae-toast small {
  margin-top: 3px;
  color: #b9c3d5;
  font-size: 10px;
}

.ae-toast-enter-active,
.ae-toast-leave-active {
  transition:
    opacity 0.2s,
    transform 0.2s;
}

.ae-toast-enter-from,
.ae-toast-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* ==========================================================
   DARK MODE — LOCAL CLASSES ONLY
   ========================================================== */

.ae-page--dark .ae-step {
  background: #303e5b;
  color: #a7befc;
}

.ae-page--dark .ae-step--violet {
  background: #403551;
  color: #c2a4f0;
}

.ae-page--dark .ae-step--amber {
  background: #493928;
  color: #f0bd7e;
}

.ae-page--dark .ae-button--danger {
  background: #452f3a;
  color: #f0a1b2;
}

.ae-page--dark .ae-switch {
  background: #566276;
}

.ae-page--dark .ae-toggle-row input:checked + .ae-switch {
  background: #547be0;
}

/* ==========================================================
   COMPACT DESKTOP
   ========================================================== */

@media (max-width: 980px) {
  .ae-layout {
    grid-template-columns: minmax(0, 1fr) 290px;
    gap: 15px;
  }

  .ae-panel {
    padding: 21px;
  }
}

/* ==========================================================
   TABLET
   ========================================================== */

@media (max-width: 760px) {
  .ae-main {
    padding: 24px 16px 66px;
  }

  .ae-hero {
    align-items: stretch;
    flex-direction: column;
    gap: 17px;
  }

  .ae-hero__actions {
    justify-content: space-between;
  }

  .ae-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .ae-sidebar {
    position: static;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 15px;
  }

  .ae-sidebar-save {
    grid-column: 1 / -1;
  }
}

/* ==========================================================
   MOBILE
   ========================================================== */

@media (max-width: 530px) {
  .ae-main {
    padding: 20px 12px 60px;
  }

  .ae-hero h1 {
    font-size: 23px;
  }

  .ae-panel {
    padding: 18px 16px;
    border-radius: 16px;
  }

  .ae-panel__heading {
    gap: 11px;
    margin-bottom: 23px;
  }

  .ae-panel__heading h2 {
    font-size: 15px;
  }

  .ae-step {
    flex-basis: 39px;
    width: 39px;
    height: 39px;
  }

  .ae-sidebar {
    display: flex;
  }

  .ae-pay-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .ae-currencies {
    width: fit-content;
  }

  .ae-experience__top {
    flex-wrap: wrap;
  }

  .ae-toast {
    left: 12px;
    right: 12px;
    bottom: 12px;
    min-width: 0;
  }
}

/* ==========================================================
   REDUCED MOTION
   ========================================================== */

@media (prefers-reduced-motion: reduce) {
  .ae-page .ae-spinner {
    animation: none;
  }

  .ae-page .ae-button,
  .ae-page .ae-switch,
  .ae-page .ae-switch::after {
    transition: none;
  }
}
</style>
